/**
 * ChatInput Component
 * Text input area for sending messages to the AI.
 * Supports Enter to send, Shift+Enter for new lines.
 * Shows Stop while running and Retry after a failure.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAIContext } from './AIContext';
import { useAppContext } from '../../contexts/Storage';

const ChatInput: React.FC = () => {
  const {
    sendMessage,
    isProcessing,
    agentStatus,
    composerDraft,
    consumeComposerDraft,
    stopGeneration,
    retryLastAction,
    failedAction,
    pendingConfirmation,
  } = useAIContext();
  const { selectedRecord } = useAppContext();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  useEffect(() => {
    if (composerDraft) {
      setInput(composerDraft);
      consumeComposerDraft();
    }
  }, [composerDraft, consumeComposerDraft]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = async () => {
    if (!input.trim() || isProcessing) return;
    const message = input;
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const statusHint = isProcessing
    ? agentStatus
      ? `${agentStatus} · Stop to cancel`
      : 'Working · Stop to cancel'
    : pendingConfirmation
      ? 'Confirm or cancel above to continue'
      : failedAction?.reason === 'network'
      ? 'Connection failed · Retry to try again'
      : 'Press Enter to send · Shift+Enter for new line';

  return (
    <div
      className="p-3 border-t"
      style={{
        borderColor: 'var(--grey-light)',
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))',
      }}
    >
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isProcessing
              ? 'Working…'
              : pendingConfirmation
                ? 'Confirm or cancel above…'
                : 'Ask anything about these books'
          }
          rows={1}
          disabled={isProcessing || !!pendingConfirmation}
          className="flex-1 resize-none rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
          style={{
            backgroundColor: 'var(--bg-grey-light)',
            color: 'var(--dark)',
            border: '1px solid transparent',
            minHeight: '40px',
            maxHeight: '120px',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--primary)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'transparent';
          }}
        />
        {isProcessing ? (
          <button
            onClick={stopGeneration}
            className="flex-shrink-0 flex items-center justify-center h-10 px-3 rounded-xl text-sm font-semibold transition-all duration-200"
            style={{
              backgroundColor: 'var(--grey-light)',
              color: 'var(--dark)',
            }}
            aria-label="Stop"
            title="Stop"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1.5" />
            </svg>
            Stop
          </button>
        ) : (
          <>
            {failedAction?.reason === 'network' && (
              <button
                onClick={() => void retryLastAction()}
                className="flex-shrink-0 flex items-center justify-center h-10 px-3 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  backgroundColor: 'var(--secondary)',
                  color: '#FFFFFF',
                }}
                aria-label="Retry"
                title="Retry"
              >
                Retry
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing}
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 disabled:opacity-40"
              style={{
                backgroundColor: 'var(--primary)',
                color: '#FFFFFF',
              }}
              aria-label="Send message"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
      {!selectedRecord && (
        <div className="mt-1 text-[10px] text-center" style={{ color: 'var(--grey-dark)' }}>
          Open your books to record an event.
        </div>
      )}
      <div
        className="mt-1 text-[10px] text-center opacity-50"
        style={{ color: 'var(--grey-dark)' }}
      >
        {statusHint}
      </div>
    </div>
  );
};

export default ChatInput;
